/**
 * Helpers for surfacing c2-manager-detected "potential" data sources on the
 * Undiscovered Data Sources page.
 *
 * The manager `/landscape` endpoint returns raw cloud resources (RDS, Azure/GCP SQL,
 * S3/Blob/GCS buckets, EC2/VM servers) carrying connectivity metadata. We:
 *   1. dedup them against the data sources the user already has (match on host + every
 *      IP/DNS variant the resource exposes), so we never list something already saved;
 *   2. shape the survivors into rows the existing UnknownSources table can render; and
 *   3. build a `?prefill=` payload so the "Create Data Source" button opens the Source
 *      Editor with everything we know pre-filled.
 *
 * No management toggles here — that lives on /manage. This page is purely "act on what
 * isn't a data source yet."
 */
import { normalizeEngineToConnectionType } from 'Utils';
import { kindForSource, inferCloud } from 'AssetManager/LandscapeUtils';
import { effectivePort } from 'DataSources/datasourcesApi';

const firstDefined = (...values) => values.find((v) => v !== undefined && v !== null && v !== '');

/** Normalize a host/endpoint/IP for case- and trailing-slash-insensitive comparison. */
export const normalizeHost = (host) => {
  if (!host) return '';
  return String(host).trim().toLowerCase().replace(/\/+$/, '');
};

/** Pull the stored host off an existing data source (top-level or nested under config). */
export const getConnectionHost = (connection = {}) =>
  firstDefined(connection?.host, connection?.config?.host, '');

/**
 * Every address a manager resource is reachable at — host plus the server instance's
 * public/private DNS + IP and any object-storage connection URL. Used for dedup so a
 * data source saved under any one of these variants suppresses the resource.
 */
export const resourceAddresses = (resource = {}) => {
  const inst = resource.instance || {};
  return [
    resource.host,
    inst.publicDnsName,
    inst.privateDnsName,
    inst.publicIpAddress,
    inst.privateIpAddress,
    resource.connectionUrl,
  ]
    .map(normalizeHost)
    .filter(Boolean);
};

/**
 * True when a data source already exists for this resource. `existingHosts` is a Set of
 * normalized hosts drawn from both the global connections list and the rows already on
 * the page (the discover risk-overview list), so we never double-list.
 */
export const isAlreadyADataSource = (resource, existingHosts) => {
  if (!existingHosts || !existingHosts.size) return false;
  return resourceAddresses(resource).some((addr) => existingHosts.has(addr));
};

/** Every normalized address a saved data source is reachable at (host + object-store rootPath). */
export const connectionAddresses = (connection = {}) =>
  [getConnectionHost(connection), connection?.rootPath, connection?.config?.rootPath]
    .map(normalizeHost)
    .filter(Boolean);

/**
 * Find the saved data source (from the global connections list) that a manager resource
 * maps to, matching on any shared host / DNS / IP / object-store URL. Returns the matched
 * connection or null. Used by the Cloud Landscape "Data source" column to link a resource
 * to its existing data source rather than offering to create a duplicate.
 */
export const findMatchingConnection = (resource = {}, connections = []) => {
  const addrs = resourceAddresses(resource);
  if (!addrs.length) return null;
  const addrSet = new Set(addrs);
  return (
    (connections || []).find((conn) =>
      connectionAddresses(conn).some((addr) => addrSet.has(addr))
    ) || null
  );
};

// --- Per-port coverage ---------------------------------------------------------------------
// A resource can legitimately host several databases on different ports (or one DB port plus
// unrelated exposures). Coverage is therefore judged per (resource, port), not per resource:
// the pipeline's Surfaced card only clears once EVERY exposed port has a data source.

/**
 * The classified exposures for a resource, or a synthetic single entry built from the
 * top-level `port` when the manager reported no exposures (managed DBs like RDS have no
 * VM to scan). Empty array when the resource exposes nothing at all.
 */
export const resourceExposures = (resource = {}) => {
  const exposures = Array.isArray(resource.portExposures) ? resource.portExposures : [];
  if (exposures.length) return exposures;
  if (resource.port != null) {
    return [{ port: resource.port, match: '', knownDatabase: resource.engine || '' }];
  }
  return [];
};

/**
 * The saved data source covering one exposed port of a resource, or null. A connection
 * covers (resource, port) when it shares an address with the resource AND its effective
 * port (explicit, or its engine's default) matches — for a collapsed range exposure,
 * falls inside the range.
 */
export const connectionForResourcePort = (resource = {}, exposure = {}, connections = []) => {
  const addrs = resourceAddresses(resource);
  if (!addrs.length) return null;
  const addrSet = new Set(addrs);
  const from = exposure.port;
  const to = exposure.portEnd != null ? exposure.portEnd : exposure.port;
  return (
    (connections || []).find((conn) => {
      if (!connectionAddresses(conn).some((addr) => addrSet.has(addr))) return false;
      const port = effectivePort(conn);
      if (port == null) return from == null; // portless source covers only a portless resource
      if (from == null) return true; // resource reported no port — any address match covers it
      return port >= from && port <= to;
    }) || null
  );
};

/** Exposures of this resource that no saved data source covers yet. */
export const uncoveredExposures = (resource = {}, connections = []) =>
  resourceExposures(resource).filter(
    (exp) => !connectionForResourcePort(resource, exp, connections)
  );

/**
 * True when the resource exposes at least one port and every exposed port is covered by a
 * saved data source — the condition for dropping its card from the pipeline's Surfaced column.
 */
export const isResourceFullyConnected = (resource = {}, connections = []) => {
  const exposures = resourceExposures(resource);
  if (!exposures.length) {
    // No port information at all: fall back to address-level matching (legacy behavior).
    return !!findMatchingConnection(resource, connections);
  }
  return exposures.every((exp) => connectionForResourcePort(resource, exp, connections));
};

/**
 * datasourceType the table's SOURCE-icon column expects, derived from the manager kind
 * plus the detected engine. `servers` resources are EC2/VM hosts found by open DB ports —
 * they're only NOSQL when the detected engine actually is mongodb; anything else on a
 * server (postgres, mysql, …) is a relational database running on a VM. DynamoDB resources
 * arrive with source "dynamodb" (kind 'rdbm', no port) — NOSQL regardless of kind.
 */
const datasourceTypeForKind = (kind, engine) => {
  if (kind === 'files') return 'FILE_FOLDER';
  const e = String(engine || '').trim().toLowerCase();
  if (e === 'dynamodb') return 'NOSQL';
  if (kind === 'servers') {
    return e === 'mongodb' ? 'NOSQL' : 'RELATIONAL';
  }
  return 'RELATIONAL';
};

/** True when a manager resource is a DynamoDB account/table listing (engine or source key). */
export const isDynamoResource = (resource = {}) =>
  String(resource.engine || resource.source || '').trim().toLowerCase() === 'dynamodb';

/**
 * Value for the IP/DNS column. Files (S3/Azure/GCS) show their object-store URL
 * (s3://… / azblob://…); databases show their endpoint host/DNS; servers prefer DNS
 * then fall back to IP.
 */
export const ipDnsForResource = (resource = {}) => {
  const kind = kindForSource(resource.source);
  if (kind === 'files') return fileResourceUrl(resource);
  const inst = resource.instance || {};
  return firstDefined(
    resource.host,
    inst.publicDnsName,
    inst.privateDnsName,
    inst.publicIpAddress,
    inst.privateIpAddress,
    '',
  ) || '';
};

/** Best host for a server/NoSQL resource: prefer DNS, fall back to IP. */
const serverHost = (resource = {}) => {
  const inst = resource.instance || {};
  return firstDefined(
    inst.publicDnsName,
    inst.privateDnsName,
    inst.publicIpAddress,
    inst.privateIpAddress,
    resource.host,
    '',
  ) || '';
};

/** Pick the file VFS scheme from the manager cloud/source key. */
const vfsSchemeForResource = (resource = {}) => {
  const src = String(resource.source || '').toLowerCase();
  if (src.includes('azure') || src.includes('blob')) return 'AZURE_BLOB';
  // s3, gcs and anything else map to the S3-style object-store editor.
  return 'S3';
};

/**
 * The rootPath / object-store URL for a file resource. Prefer the manager-supplied
 * `connectionUrl` (azblob://account/container/, and s3://bucket/ from newer managers).
 * Older managers leave S3 buckets with only `name`/`identifier`, so fall back to
 * building `s3://<bucket>/` ourselves. This is also the value shown in the IP/DNS column.
 */
export const fileResourceUrl = (resource = {}) => {
  if (resource.connectionUrl) return resource.connectionUrl;
  const scheme = vfsSchemeForResource(resource);
  const bucket = firstDefined(resource.name, resource.identifier, '');
  if (scheme === 'S3' && bucket) return `s3://${bucket}/`;
  return '';
};

/**
 * Build the `?prefill=` payload (pre-base64) for a manager resource. Shape matches what
 * `SourceEditor.decodePrefill` consumes — `type` is either a `FILE_*` enum (file editor)
 * or an engine string (DB editor).
 */
export const managerSourceToEditorPrefill = (resource = {}, overrides = {}) => {
  const kind = kindForSource(resource.source);
  const name = firstDefined(resource.name, resource.database, resource.identifier, '');
  // Per-port callers (the resource ports page) pin the specific port/engine of one
  // exposure; without overrides the resource's own top-level values apply as before.
  const overridePort = overrides.port != null ? overrides.port : null;
  const overrideEngine = overrides.engine || null;

  if (kind === 'files') {
    const vfsScheme = vfsSchemeForResource(resource);
    return {
      type: 'FILE_GENERIC_FOLDER',
      vfsScheme,
      rootPath: fileResourceUrl(resource),
      region: resource.region || '',
      name,
    };
  }

  if (kind === 'servers') {
    // Servers discovered by open DB ports — treat as a generic DB host; default to the
    // discovered engine if present, otherwise leave engine for the user to pick.
    const engine = normalizeEngineToConnectionType(overrideEngine || resource.engine) || '';
    return {
      type: engine,
      name,
      host: serverHost(resource),
      port: overridePort != null ? overridePort : (resource.port != null ? resource.port : ''),
      database: resource.database || '',
      user: resource.user || '',
    };
  }

  // DynamoDB — region-addressed, no host/port/database. Prefill just the engine, name,
  // and region; credentials default to the platform's AWS identity in the editor.
  if (isDynamoResource(resource)) {
    return {
      type: 'dynamodb',
      name,
      region: resource.region || '',
    };
  }

  // rdbm — relational / managed database.
  const engine = normalizeEngineToConnectionType(overrideEngine || resource.engine) || '';
  return {
    type: engine,
    name,
    host: resource.host || '',
    port: overridePort != null ? overridePort : (resource.port != null ? resource.port : ''),
    database: resource.database || '',
    schemaName: resource.schema || '',
    user: resource.user || '',
    region: resource.region || '',
  };
};

/** Base64-encode a prefill payload for the `?prefill=` query param (mirror of decodePrefill). */
export const encodePrefill = (obj) => btoa(JSON.stringify(obj));

/**
 * Shape a manager resource into a row the UnknownSources table renders. Tagged
 * `_potential` so the actions column shows a single "Add" button and carries the raw
 * `resource` for prefill. The original manager `source` key and `cloud` are preserved
 * (not collapsed to a legacy key) so the CLOUD/SOURCE columns render the right branded
 * icons. Columns consumed: connectionName, cloud, region, source, ipDns, engine, database, schema.
 */
export const managerSourceToRow = (resource = {}) => {
  const kind = kindForSource(resource.source);
  return {
    _potential: true,
    resource,
    connectionName: firstDefined(resource.name, resource.database, resource.identifier, ''),
    cloud: resource.cloud || inferCloud(resource.source),
    source: resource.source || '',
    datasourceType: datasourceTypeForKind(kind, resource.engine || resource.source),
    ipDns: ipDnsForResource(resource),
    engine: resource.engine || '',
    region: resource.region || '',
    database: resource.database || '',
    schema: resource.schema || '',
  };
};
